import type { GetStaticPaths, GetStaticProps, NextPage } from 'next'

import {
  DomainOrSkillPage,
  DomainOrSkillPageProps,
  DomainSkillEntity,
} from 'src/App/DomainOrSkillPage/DomainOrSkillPage'
import { CURRENT_DATA_DOMAINS, DomainType } from 'src/data/domainData/domains'
import { CURRENT_DATA_SKILLS } from 'src/data/skillsData/skills'
import { getResumesByIds } from 'src/graphql/queries/resume/api/getResumesById'
import { Resume } from 'src/graphql/types'
import { strapiGetStories } from 'src/strapi/api/getStories'
import { StoryClientType } from 'src/strapi/types/storiesTypes'

const DomainOrSkill: NextPage<DomainOrSkillPageProps> = (
  props: DomainOrSkillPageProps
) => <DomainOrSkillPage key={props.domainOrSkillEntity.url} {...props} />

export const getStaticProps: GetStaticProps<DomainOrSkillPageProps> = async (
  context
) => {
  const { slug } = context.params as Record<'slug', string>

  const foundDomain = CURRENT_DATA_DOMAINS.find(({ url }) => url === slug)
  const foundSkill = CURRENT_DATA_SKILLS.find(({ url }) => url === slug)
  const domainOrSkillEntity = foundDomain || foundSkill

  const resumes: Array<Resume> = []
  const stories: Array<StoryClientType> = []

  try {
    const fetchedResumes = await getResumesByIds(
      domainOrSkillEntity?.resumesIds || []
    )
    resumes.push(...fetchedResumes)

    const fetchedStories = await strapiGetStories({
      withImages: true,
      page: 1,
      pageSize: 6,
    })
    stories.push(...fetchedStories)

    if (!stories.length) {
      throw new Error('Stories were not received')
    }
  } catch (e) {
    console.error(
      'Failed to load resumes building Domain page, ',
      `ID = ${slug}`,
      e
    )
  }

  if (!domainOrSkillEntity || !resumes) {
    return {
      notFound: true,
    }
  }

  // NOTICE some props aren't serializable and page doesn't require them
  const _removeUnneededNotSerializableProps = (entity: DomainSkillEntity) => {
    delete (entity as DomainType)['iconCmp']
  }
  _removeUnneededNotSerializableProps(domainOrSkillEntity)

  return {
    revalidate: false,
    props: {
      domainOrSkillEntity,
      resumes,
      stories,
    },
  }
}

export const getStaticPaths: GetStaticPaths = async () => {
  const getSlugs = (data: Array<Pick<DomainSkillEntity, 'url'>>) => {
    return data.map(({ url }) => ({
      params: {
        slug: url,
      },
    }))
  }

  const domainSlugs = getSlugs(CURRENT_DATA_DOMAINS)
  const skillsSlugs = getSlugs(CURRENT_DATA_SKILLS)

  const paths = domainSlugs.concat(skillsSlugs)

  return {
    paths,
    fallback: false,
  }
}

export default DomainOrSkill
